import { useEffect, useMemo } from 'preact/hooks';
import { html } from '@shared/utils/rendering/html';
import { useAuth } from '../context/auth-context';
import { useFeatureChecks } from '../features/feature-context';
import { SystemKeys } from '../../constants/security/systems.constants';

export default function withRoleGuard(Wrapped, options = {}) {
    const { system = '', required = [], mode = 'any', fallback: FallbackComponent } = options;

    const Guard = (props) => {
        const context = useAuth();
        if (!context) {
            console.error(
                '[auth] useAuth() returned null. Likely duplicate contexts or provider not mounted.'
            );
            return html`<div class="error-page">
                Auth wiring error: missing AuthProvider or duplicate context instance.
            </div>`;
        }

        const { ready, isAuthenticated, user, login } = context;
        const { map: featureMap = {} } = useFeatureChecks();

        const roles = useMemo(() => {
            const r = user?.roles || user?.permissions || [];
            return Array.isArray(r) ? r : [];
        }, [user]);

        const systems = useMemo(() => {
            const r = user?.remoteSystems || user?.permissions || [];
            return Array.isArray(r) ? r : [];
        }, [user]);

        const isSystemFeatureEnabled = useMemo(() => {
            if (!system) return true;

            const key = SystemKeys[system];
            if (!key) return true;

            return Object.prototype.hasOwnProperty.call(featureMap, key)
                ? Boolean(featureMap[key])
                : true;
        }, [featureMap, system]);

        const isAllowed = useMemo(() => {
            if (!isSystemFeatureEnabled) return false;

            console.debug('User has roles: ', roles);
            if (typeof required === 'function') return required(user || null);

            const haveSystems = new Set(systems);
            if (system && !haveSystems.has(system)) return false;

            const needed = Array.isArray(required) ? required : [required];
            if (!needed.length) return true;

            const have = new Set(roles);
            if (mode === 'all') return needed.every((role) => have.has(role));

            // default 'any'
            return needed.some((role) => have.has(role));
        }, [isSystemFeatureEnabled, mode, required, roles, system, systems, user]);

        useEffect(() => {
            if (ready && !isAuthenticated) {
                const returnUrl = location.pathname + location.search + location.hash;
                login(returnUrl);
            }
        }, [ready, isAuthenticated]);

        if (!ready) {
            return html`
                <div class="authenticating p-4">
                    <h2 class="mb-2">Authenticating your session...</h2>
                    <p>We are just verifying who you are, this should not take long.</p>
                </div>
            `;
        }

        if (!isAuthenticated) {
            return html`
                <div class="redirecting p-4">
                    <h2 class="mb-2">Redirecting you to the sign in page</h2>
                    <p>Please wait a moment...</p>
                </div>
            `;
        }

        if (!isAllowed) {
            if (FallbackComponent) return html`<${FallbackComponent} />`;

            window.dispatchEvent(new CustomEvent('api:forbidden-error'));

            return html`
                <div class="access-denied p-4">
                    <h2 class="mb-2">403 — Access Denied</h2>
                    <p>You do not have permission to access this resource.</p>
                </div>
            `;
        }

        return html`<${Wrapped} ...${props} />`;
    };

    Guard.displayName = `WithRoleGuard(${Wrapped.displayName || Wrapped.name || 'Component'})`;
    return Guard;
}
